// Player culture on the client: interface language, time zone, 12/24 h clock and formats (Intl).
// Preferences live in the profile (synchronised with the account); resolution is done by @gruvetto/core.
// Usage: `t.home.title`, `t.common.rides(n)`, `fmtDate(ts)`. Core texts go through ./content.js.
import { resolveCulture, startOfDay, daysBetween, DEFAULT_CULTURE, type Culture, type CulturePrefs, type Lang } from '@gruvetto/core';
import en, { type Dict } from './en/index.js';

export type { Dict };
/** Name of each language in its own language (language selector). */
export const LANG_NAMES: Record<Lang, string> = { en: 'English', fr: 'Français' };
/** Loaded dictionaries: English is in the bundle, the others come as their own chunk (loadDict). */
const DICTS: Partial<Record<Lang, Dict>> = { en };
const LOADERS: Record<Lang, () => Promise<{ default: Dict }>> = { en: async () => ({ default: en }), fr: () => import('./fr/index.js') };

/** Fetches a language's dictionary if it is not in memory yet (main.ts before the first screen, then on a language change). */
export async function loadDict(l: Lang): Promise<Dict> {
  if (!DICTS[l]) {
    DICTS[l] = (await LOADERS[l]()).default;
    // The active culture may already be this language (browser default): texts computed from the English fallback follow.
    if (l === current.lang) for (const fn of listeners) fn(current, true);
  }
  return DICTS[l]!;
}
export const dictLoaded = (l: Lang): boolean => !!DICTS[l];

/** Device environment: preferred languages and detected time zone. */
export function deviceEnv(): { languages: readonly string[]; timeZone: string } {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  let timeZone = 'UTC';
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    /* engine without full Intl */
  }
  return { languages: nav?.languages?.length ? nav.languages : nav?.language ? [nav.language] : [], timeZone };
}

let current: Culture = resolveCulture(DEFAULT_CULTURE, deviceEnv());
const listeners = new Set<(c: Culture, langChanged: boolean) => void>();

/** Active culture (resolved language, regional locale, time zone, clock). */
export const culture = (): Culture => current;
export const lang = (): Lang => current.lang;
export const tz = (): string => current.timeZone;
export const onCultureChange = (fn: (c: Culture, langChanged: boolean) => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/**
 * Applies preferences; returns true if the effective culture changes. A language whose dictionary is not loaded yet
 * takes effect once it is (the listeners then run), so no screen is ever drawn half translated.
 */
export function applyCulture(prefs: Partial<CulturePrefs> | undefined): boolean {
  const next = resolveCulture(prefs, deviceEnv());
  if (JSON.stringify(next) === JSON.stringify(current)) return false;
  if (DICTS[next.lang]) commit(next);
  else loadDict(next.lang).then(() => commit(next)).catch(() => commit({ ...next, lang: 'en' }));
  return true;
}
function commit(next: Culture) {
  const langChanged = next.lang !== current.lang;
  current = next;
  fmtCache.clear();
  if (typeof document !== 'undefined') document.documentElement.lang = next.locale;
  for (const fn of listeners) fn(next, langChanged);
}

/** Dictionary of the active language. Lazy access: `t.x.y` follows a language change without reloading. */
export const t: Dict = new Proxy({} as Dict, { get: (_, k: string) => ((DICTS[current.lang] ?? en) as any)[k] });
/** Dictionary of a given language, English until that one is loaded (tests, checks on the dictionaries). */
export const dictFor = (l: Lang): Dict => DICTS[l] ?? en;

// ---------- Formats ----------
const fmtCache = new Map<string, unknown>();
const cached = <T>(key: string, make: () => T): T => {
  if (!fmtCache.has(key)) fmtCache.set(key, make() as any);
  return fmtCache.get(key) as T;
};
/** Effective 24 h clock (explicit choice or locale convention): the hour is then written with two digits, “09:30”. */
const is24h = () =>
  cached('h24', () => ({
    v: !/^h1[12]$/.test(new Intl.DateTimeFormat(current.locale, { hour: 'numeric', hour12: current.hour12 }).resolvedOptions().hourCycle ?? ''),
  })).v;
const dtf = (name: string, o: Intl.DateTimeFormatOptions) =>
  cached(
    `d:${name}`,
    () =>
      new Intl.DateTimeFormat(current.locale, {
        timeZone: current.timeZone,
        ...o,
        ...(o.hour ? { hour: is24h() ? '2-digit' : 'numeric', hour12: current.hour12 } : {}),
      }),
  );

/** Number according to the locale (thousands and decimal separators). */
export const fmtNumber = (n: number, digits?: number): string =>
  // the HUD calls this dozens of times a frame: the key is built without serialising options
  cached(`nd:${digits ?? ''}`, () => new Intl.NumberFormat(current.locale, digits == null ? {} : { minimumFractionDigits: digits, maximumFractionDigits: digits })).format(n || 0);
/** “Tue, 27 Oct” */
export const fmtDate = (ts: number): string => dtf('date', { weekday: 'short', day: 'numeric', month: 'short' }).format(ts);
/** “Tuesday 27 October” */
export const fmtDateLong = (ts: number): string => dtf('dateLong', { weekday: 'long', day: 'numeric', month: 'long' }).format(ts);
/** “27 Oct” (chart axes) */
export const fmtDayMonth = (ts: number): string => dtf('dayMonth', { day: 'numeric', month: 'short' }).format(ts);
/** “October 2026” */
export const fmtMonthYear = (ts: number): string => dtf('monthYear', { month: 'long', year: 'numeric' }).format(ts);
/** Wall-clock time “14:30” or “2:30 PM” depending on the chosen clock. */
export const fmtTimeOfDay = (ts: number): string => dtf('time', { hour: 'numeric', minute: '2-digit' }).format(ts);
/** “Tuesday 27 October, 14:30” */
export const fmtDateTime = (ts: number): string =>
  dtf('dateTime', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' }).format(ts);
/** “today”, “yesterday”, “3 days ago”, then the date beyond a week (days in the player's time zone). */
export function fmtRelativeDay(ts: number, now = Date.now()): string {
  const days = daysBetween(ts, now, current.timeZone);
  if (days >= 7 || days < 0) return fmtDate(ts);
  return cached('rel', () => new Intl.RelativeTimeFormat(current.locale, { numeric: 'auto' })).format(-days, 'day');
}
/** Plural category of the locale (“one”, “other”…). */
export const pluralCat = (n: number): Intl.LDMLPluralRule => cached('pl', () => new Intl.PluralRules(current.locale)).select(n);
/** Ordinal rank: “1st”, “2nd” / “1er”, “2e”. */
export const fmtOrdinal = (n: number): string => t.common.ordinal(n, cached('ord', () => new Intl.PluralRules(current.locale, { type: 'ordinal' })).select(n));
/** Readable duration: “1 h 05”, “7 min 30 s”, “45 s”. */
export const fmtDur = (s: number): string => {
  s = Math.round(s || 0);
  const h = Math.floor(s / 3600),
    m = Math.floor((s % 3600) / 60),
    x = s % 60;
  return h
    ? t.common.durHM(h, String(m).padStart(2, '0'))
    : m
      ? x && m < 10
        ? t.common.durMS(m, String(x).padStart(2, '0'))
        : t.common.durM(m)
      : t.common.durS(x);
};
/** Stopwatch “1:05:09” / “5:09” (same in every language). */
export const fmtClock = (s: number): string => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600),
    m = Math.floor((s % 3600) / 60),
    x = s % 60;
  return (h ? h + ':' + String(m).padStart(2, '0') : m) + ':' + String(x).padStart(2, '0');
};
/** Distance “12.3 km”; no decimal beyond 100 km. */
export const fmtKm = (m: number): string => `${fmtNumber((m || 0) / 1000, (m || 0) >= 100000 ? 0 : 1)} km`;
/** IANA time zones offered to the player (engine list, or at least the detected zone and UTC). */
export function timeZones(): string[] {
  let list: string[];
  try {
    list = (Intl as any).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    list = [];
  }
  const set = new Set([...list, deviceEnv().timeZone, current.timeZone, 'UTC']);
  return [...set].sort();
}
/** Start of the day in the player's time zone. */
export const today = (now = Date.now()): number => startOfDay(now, current.timeZone);
